import { ScrollText } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Empty } from "@/components/ui/empty"
import type { AgentChangelogEntry } from "@/types"

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  const month = date.toLocaleDateString("en-US", { month: "short" })
  const day = date.getDate()
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
  return `${month} ${day}, ${time}`
}

interface AgentChangelogProps {
  entries: AgentChangelogEntry[]
}

export default function AgentChangelog({ entries }: AgentChangelogProps) {
  const [visibleCount, setVisibleCount] = useState(3)

  if (!entries || entries.length === 0) {
    return (
      <Card className="px-6 shadow-none">
        <Empty
          icon={<ScrollText className="size-4" />}
          title="No updates yet"
          description="Imperative will post updates here when there are changes or notices related to this agent."
        />
      </Card>
    )
  }

  const visibleEntries = entries.slice(0, visibleCount)
  const hasMore = entries.length > visibleCount

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 5, entries.length))
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="bg-border absolute top-0 bottom-0 left-[0.20rem] w-0.5" />

      <div className="space-y-6">
        {visibleEntries.map((entry) => (
          <div key={entry.uid} className="relative flex gap-4">
            {/* Timeline dot */}
            <div className="relative left-1 z-10 flex h-2.5 w-2.5 -translate-x-1/2 items-center justify-center">
              <div className="bg-border h-2.5 w-2.5 rounded-full" />
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              <div className="text-muted-foreground mb-1 text-sm font-medium">
                {formatDateTime(entry.created_at)}
              </div>
              <div className="mb-1 font-semibold">{entry.title}</div>
              <div className="text-muted-foreground text-sm">{entry.body}</div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && visibleCount < entries.length && (
        <div className="mt-4 px-4">
          <Button variant="ghost" onClick={handleLoadMore} className="w-full">
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}
